/**
 * Admin Cost Tracking Page
 *
 * Provider cost tracking with margin analysis and breakdowns.
 *
 * @module @humanizer/studio/components/admin/AdminCosts
 */

import { useState, useEffect, useCallback } from 'react';
import { useApi } from '../../contexts/ApiContext';
import { createLogger } from '../../utils/debug';

const log = createLogger('AdminCosts');

// ═══════════════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface CostEntry {
  date: string;
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  providerCostCents: number;
  userChargedCents: number;
  marginCents: number;
}

interface CostSummary {
  totalProviderCost: number;
  totalUserCharged: number;
  totalMargin: number;
  marginPercentage: number;
}

interface ProviderBreakdown {
  provider: string;
  cost: number;
  charged: number;
  margin: number;
  requests: number;
}

interface DailyCost {
  date: string;
  providerCost: number;
  userCharged: number;
}

interface CostData {
  summary: CostSummary;
  providers: ProviderBreakdown[];
  daily: DailyCost[];
  recentEntries: CostEntry[];
}

const EMPTY_COST_DATA: CostData = {
  summary: { totalProviderCost: 0, totalUserCharged: 0, totalMargin: 0, marginPercentage: 0 },
  providers: [],
  daily: [],
  recentEntries: [],
};

// ═══════════════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════════════

export function AdminCosts() {
  const api = useApi();

  // State
  const [data, setData] = useState<CostData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<'7d' | '30d' | '90d'>('30d');

  // ─────────────────────────────────────────────────────────────────────────
  // DATA FETCHING
  // ─────────────────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      // Calculate date range based on period
      const now = new Date();
      const daysBack = period === '7d' ? 7 : period === '30d' ? 30 : 90;
      const startDate = new Date(now.getTime() - daysBack * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      const endDate = now.toISOString().split('T')[0];

      const result = await api.admin.getCostAnalytics({ startDate, endDate });

      if (result.totalProviderCostMillicents !== undefined) {
        // Convert byProvider object to ProviderBreakdown array
        const providerArray: ProviderBreakdown[] = result.byProvider
          ? Object.entries(result.byProvider).map(([provider, data]) => {
              const costCents = Math.floor((data.cost ?? 0) / 10);
              const chargedCents = Math.floor((data.charged ?? 0) / 10);
              return {
                provider,
                cost: costCents,
                charged: chargedCents,
                margin: chargedCents - costCents,
                requests: data.requests ?? 0,
              };
            })
          : [];

        setData({
          summary: {
            totalProviderCost: Math.floor(result.totalProviderCostMillicents / 10),
            totalUserCharged: Math.floor((result.totalChargedMillicents ?? 0) / 10),
            totalMargin: Math.floor((result.marginMillicents ?? 0) / 10),
            marginPercentage: result.marginPercent ?? 0,
          },
          providers: providerArray,
          daily: result.daily?.map((d) => ({
            date: d.date,
            providerCost: Math.floor(d.cost / 10),
            userCharged: Math.floor(d.charged / 10),
          })) ?? [],
          recentEntries: (result.recentTransactions ?? []).map((t) => {
            const providerCostCents = Math.floor(t.providerCostMillicents / 10);
            const userChargedCents = Math.floor(t.userChargeMillicents / 10);
            return {
              date: t.createdAt,
              provider: t.provider,
              model: t.model,
              inputTokens: t.inputTokens,
              outputTokens: t.outputTokens,
              providerCostCents,
              userChargedCents,
              marginCents: userChargedCents - providerCostCents,
            };
          }),
        });
      } else {
        setData(EMPTY_COST_DATA);
      }
    } catch (err) {
      log.warn('Cost analytics endpoint not available:', err);
      setError('Cost tracking service unavailable.');
    } finally {
      setLoading(false);
    }
  }, [api, period]);

  useEffect(() => {
    fetchData();
  }, [fetchData, period]);

  // ─────────────────────────────────────────────────────────────────────────
  // HELPERS
  // ─────────────────────────────────────────────────────────────────────────

  const formatCost = (cents: number) => `$${(cents / 100).toFixed(2)}`;

  const formatNumber = (n: number) => {
    if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
    if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
    return n.toString();
  };

  const getBarWidth = (value: number, max: number) => {
    return `${Math.max((value / max) * 100, 2)}%`;
  };

  const getDaysForPeriod = () => {
    switch (period) {
      case '7d':
        return 7;
      case '30d':
        return 30;
      case '90d':
        return 90;
      default:
        return 30;
    }
  };

  const filteredDaily = data?.daily.slice(-getDaysForPeriod()) || [];
  const maxDailyCost = Math.max(...filteredDaily.map((d) => d.userCharged), 1);

  // ─────────────────────────────────────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="admin-costs">
        <div className="admin-loading">
          <span className="admin-loading__spinner" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="admin-costs">
        <div className="admin-alert admin-alert--error">
          <span className="admin-alert__icon">!</span>
          <div className="admin-alert__content">
            <p className="admin-alert__message">{error || 'Failed to load cost data'}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-costs">
      {/* Header */}
      <div className="admin-costs__header">
        <div className="admin-costs__header-left">
          <h1 className="admin-costs__title">Cost Tracking</h1>
          <div className="admin-costs__period-toggle">
            <button
              className={`admin-costs__period-btn ${period === '7d' ? 'admin-costs__period-btn--active' : ''}`}
              onClick={() => setPeriod('7d')}
            >
              7 days
            </button>
            <button
              className={`admin-costs__period-btn ${period === '30d' ? 'admin-costs__period-btn--active' : ''}`}
              onClick={() => setPeriod('30d')}
            >
              30 days
            </button>
            <button
              className={`admin-costs__period-btn ${period === '90d' ? 'admin-costs__period-btn--active' : ''}`}
              onClick={() => setPeriod('90d')}
            >
              90 days
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="admin-costs__summary">
        <div className="admin-costs__card">
          <div className="admin-costs__card-label">Provider Cost</div>
          <div className="admin-costs__card-value">{formatCost(data.summary.totalProviderCost)}</div>
          <div className="admin-costs__card-sub">Our expense to providers</div>
        </div>
        <div className="admin-costs__card">
          <div className="admin-costs__card-label">User Charged</div>
          <div className="admin-costs__card-value">{formatCost(data.summary.totalUserCharged)}</div>
          <div className="admin-costs__card-sub">Revenue from users</div>
        </div>
        <div className="admin-costs__card admin-costs__card--highlight">
          <div className="admin-costs__card-label">Gross Margin</div>
          <div className="admin-costs__card-value">{formatCost(data.summary.totalMargin)}</div>
          <div className="admin-costs__card-sub admin-costs__card-sub--success">
            {data.summary.marginPercentage.toFixed(1)}% margin
          </div>
        </div>
      </div>

      {/* Content Grid */}
      <div className="admin-costs__content">
        {/* Daily Cost Chart */}
        <div className="admin-costs__section">
          <h2 className="admin-costs__section-title">Daily Revenue vs Cost</h2>
          <div className="admin-costs__chart">
            <div className="admin-costs__chart-bars">
              {filteredDaily.map((day) => (
                <div key={day.date} className="admin-costs__chart-bar-group">
                  <div
                    className="admin-costs__chart-bar admin-costs__chart-bar--charged"
                    style={{ height: getBarWidth(day.userCharged, maxDailyCost) }}
                    title={`${day.date}: ${formatCost(day.userCharged)} charged`}
                  />
                  <div
                    className="admin-costs__chart-bar admin-costs__chart-bar--cost"
                    style={{ height: getBarWidth(day.providerCost, maxDailyCost) }}
                    title={`${day.date}: ${formatCost(day.providerCost)} cost`}
                  />
                </div>
              ))}
            </div>
            <div className="admin-costs__chart-labels">
              <span>{filteredDaily[0]?.date}</span>
              <span>{filteredDaily[filteredDaily.length - 1]?.date}</span>
            </div>
            <div className="admin-costs__chart-legend">
              <span className="admin-costs__legend-item">
                <span className="admin-costs__legend-dot admin-costs__legend-dot--charged" />
                User Charged
              </span>
              <span className="admin-costs__legend-item">
                <span className="admin-costs__legend-dot admin-costs__legend-dot--cost" />
                Provider Cost
              </span>
            </div>
          </div>
        </div>

        {/* Provider Breakdown */}
        <div className="admin-costs__section">
          <h2 className="admin-costs__section-title">By Provider</h2>
          {data.providers.length === 0 ? (
            <div className="admin-empty">
              <span className="admin-empty__icon">P</span>
              <h3 className="admin-empty__title">Provider Breakdown Unavailable</h3>
              <p className="admin-empty__description">
                Per-provider cost breakdown will be available with enhanced analytics.
              </p>
            </div>
          ) : (
            <div className="admin-costs__providers">
              {data.providers.map((provider) => {
                const maxCost = Math.max(...data.providers.map((p) => p.charged));
                return (
                  <div key={provider.provider} className="admin-costs__provider">
                    <div className="admin-costs__provider-header">
                      <span className="admin-costs__provider-name">{provider.provider}</span>
                      <span className="admin-costs__provider-requests">
                        {formatNumber(provider.requests)} requests
                      </span>
                    </div>
                    <div className="admin-costs__provider-bar">
                      <div
                        className="admin-costs__provider-fill admin-costs__provider-fill--charged"
                        style={{ width: getBarWidth(provider.charged, maxCost) }}
                      />
                      <div
                        className="admin-costs__provider-fill admin-costs__provider-fill--cost"
                        style={{ width: getBarWidth(provider.cost, maxCost) }}
                      />
                    </div>
                    <div className="admin-costs__provider-meta">
                      <span>Cost: {formatCost(provider.cost)}</span>
                      <span>Charged: {formatCost(provider.charged)}</span>
                      <span className="admin-costs__provider-margin">
                        Margin: {formatCost(provider.margin)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Recent Transactions */}
        <div className="admin-costs__section">
          <h2 className="admin-costs__section-title">Recent Transactions</h2>
          {data.recentEntries.length === 0 ? (
            <div className="admin-empty">
              <span className="admin-empty__icon">$</span>
              <h3 className="admin-empty__title">No Recent Transactions</h3>
              <p className="admin-empty__description">
                Transaction details will appear as API requests are processed.
              </p>
            </div>
          ) : (
            <div className="admin-costs__table">
              <div className="admin-costs__table-header">
                <span className="admin-costs__table-cell admin-costs__table-cell--time">Time</span>
                <span className="admin-costs__table-cell admin-costs__table-cell--model">Model</span>
                <span className="admin-costs__table-cell admin-costs__table-cell--tokens">Tokens</span>
                <span className="admin-costs__table-cell admin-costs__table-cell--num">Cost</span>
                <span className="admin-costs__table-cell admin-costs__table-cell--num">Charged</span>
                <span className="admin-costs__table-cell admin-costs__table-cell--num">Margin</span>
              </div>
              {data.recentEntries.map((entry, index) => (
                <div key={index} className="admin-costs__table-row">
                  <span className="admin-costs__table-cell admin-costs__table-cell--time">
                    {new Date(entry.date).toLocaleTimeString()}
                  </span>
                  <span className="admin-costs__table-cell admin-costs__table-cell--model">
                    <span className="admin-costs__model-provider">{entry.provider}</span>
                    <span className="admin-costs__model-name">{entry.model}</span>
                  </span>
                  <span className="admin-costs__table-cell admin-costs__table-cell--tokens">
                    {formatNumber(entry.inputTokens + entry.outputTokens)}
                  </span>
                  <span className="admin-costs__table-cell admin-costs__table-cell--num">
                    {formatCost(entry.providerCostCents)}
                  </span>
                  <span className="admin-costs__table-cell admin-costs__table-cell--num">
                    {formatCost(entry.userChargedCents)}
                  </span>
                  <span className="admin-costs__table-cell admin-costs__table-cell--num admin-costs__table-cell--margin">
                    {formatCost(entry.marginCents)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
